import { db } from '../instant';
import { id } from '@instantdb/react';
import { InventoryHistoryType } from '../schema';
import {
    computeConsumptionDelta,
    computeItemLocationQuantityAfterConsumptionDelta,
    computeItemLocationQuantityAfterChange,
} from './inventoryHistoryLogic';

interface RecordInventoryChangeParams {
    itemTypeId: string;
    type: InventoryHistoryType;
    quantity: number;
    note?: string;
    eventId?: string | null;
    storageLocationId?: string | null;
    /** Pre-loaded itemLocation record. When provided, skips the network fetch. */
    existingItemLocation?: { id: string; quantity: number } | null;
}

interface RecordConsumptionParams {
    itemTypeId: string;
    quantityUsed: number;
    note?: string;
    eventId: string;
    /** Pre-loaded existing consumption history record. When provided, skips the network fetch. */
    existingHistory?: { id: string; quantityChange: number } | null;
    /** Pre-loaded itemLocation record for this item at this event. When provided, skips the network fetch. */
    existingItemLocation?: { id: string; quantity: number } | null;
}

/**
 * Record consumption for a consumable item at an event.
 * Supports editing existing consumption records by calculating the delta.
 *
 * @param params - Consumption parameters
 * @param params.itemTypeId - ID of the consumable item type
 * @param params.quantityUsed - Quantity used (positive number)
 * @param params.note - Optional note
 * @param params.eventId - Event ID where consumption occurred
 * @param params.existingHistory - Optional pre-loaded existing history record to update
 */
export async function recordConsumption(params: RecordConsumptionParams): Promise<void> {
    const { itemTypeId, quantityUsed, note, eventId, existingHistory, existingItemLocation } = params;

    // Validate quantity
    if (quantityUsed < 0) {
        throw new Error('Quantity used must be greater than or equal to 0');
    }

    // Use pre-loaded existing history record (avoids network fetch, works offline)
    const existingRecord = existingHistory ?? null;

    const { delta } = computeConsumptionDelta(existingRecord ? existingRecord.quantityChange : null, quantityUsed);

    // Use pre-loaded itemLocation (avoids network fetch, works offline)
    const existingLocation = existingItemLocation ?? null;
    let locationId: string;

    if (existingLocation) {
        locationId = existingLocation.id;
    } else {
        // Create new location
        locationId = id();
    }

    const txs: any[] = [];

    // Update or create inventoryHistory entry
    if (existingRecord) {
        // Update existing history record
        txs.push(
            db.tx.inventoryHistory[existingRecord.id].update({
                quantityChange: -quantityUsed, // Store as negative
                note: note || undefined,
                timestamp: new Date(),
            })
        );
    } else {
        // Create new history record
        const historyId = id();
        txs.push(
            db.tx.inventoryHistory[historyId].update({
                quantityChange: -quantityUsed, // Store as negative
                type: 'consumed',
                note: note || undefined,
                timestamp: new Date(),
            })
        );
        txs.push(db.tx.inventoryHistory[historyId].link({ itemType: itemTypeId }));
        txs.push(db.tx.inventoryHistory[historyId].link({ event: eventId }));
    }

    const currentQuantity = existingLocation?.quantity || 0;
    const newQuantity = computeItemLocationQuantityAfterConsumptionDelta(currentQuantity, delta);

    if (existingLocation) {
        txs.push(
            db.tx.itemLocations[locationId].update({
                quantity: newQuantity,
            })
        );
    } else {
        txs.push(
            db.tx.itemLocations[locationId].update({
                quantity: newQuantity,
            })
        );
        txs.push(db.tx.itemLocations[locationId].link({ itemType: itemTypeId }));
        txs.push(db.tx.itemLocations[locationId].link({ event: eventId }));
    }

    // Execute atomic transaction
    await db.transact(txs);
}

/**
 * Record an inventory history change (purchase, lost, broken, consumed)
 * and update the corresponding itemLocation quantity.
 *
 * For purchases: quantity should be positive, increases inventory
 * For lost/broken/consumed: quantity should be positive (will be stored as negative)
 */
export async function recordInventoryChange(params: RecordInventoryChangeParams): Promise<void> {
    const { itemTypeId, type, quantity, note, eventId, storageLocationId, existingItemLocation } = params;

    if (type === 'adjustment') {
        // For adjustments, quantity is the signed delta (can be positive or negative, but not zero)
        if (quantity === 0) {
            throw new Error('Adjustment quantity must not be zero');
        }
    } else if (quantity <= 0) {
        throw new Error('Quantity must be greater than 0');
    }

    const quantityChange = type === 'adjustment' ? quantity : type === 'purchase' ? quantity : -quantity;

    // Use pre-loaded itemLocation (avoids network fetch, works offline)
    let locationId: string;
    const existingLocation = existingItemLocation ?? null;

    if (existingLocation) {
        locationId = existingLocation.id;
    } else {
        locationId = id();
    }

    const txs: any[] = [];
    const currentQuantity = existingLocation?.quantity || 0;
    const newQuantity = computeItemLocationQuantityAfterChange(currentQuantity, quantityChange);

    if (existingLocation) {
        txs.push(db.tx.itemLocations[locationId].update({ quantity: newQuantity }));
    } else {
        txs.push(db.tx.itemLocations[locationId].update({ quantity: newQuantity }));
        txs.push(db.tx.itemLocations[locationId].link({ itemType: itemTypeId }));
        if (eventId) {
            txs.push(db.tx.itemLocations[locationId].link({ event: eventId }));
        }
        if (storageLocationId) {
            txs.push(db.tx.itemLocations[locationId].link({ storageLocation: storageLocationId }));
        }
    }

    const historyId = id();
    txs.push(
        db.tx.inventoryHistory[historyId].update({
            quantityChange,
            type,
            note: note || undefined,
            timestamp: new Date(),
        })
    );
    txs.push(db.tx.inventoryHistory[historyId].link({ itemType: itemTypeId }));
    if (eventId) {
        txs.push(db.tx.inventoryHistory[historyId].link({ event: eventId }));
    }
    if (storageLocationId) {
        txs.push(db.tx.inventoryHistory[historyId].link({ storageLocation: storageLocationId }));
    }

    await db.transact(txs);
}
